/**
 * Duplicate A Past Job — the monolith's #dupModal, ported whole
 * (legacy/smart-email-request/index.html L1693–1697 markup + L5213–5335 engine):
 *
 *   - header search ("Search by name, community, or subject…") live-filtering
 *     over project + community + subject
 *   - stale-while-revalidate open: prefetch cache → localStorage → live /jobs
 *     refresh in the background (2026-07-22, per Jack: "modal felt slow")
 *   - past-only rows ("PAST job means deployed") + the MVP template filter
 *   - row = division badge with the ☆/★ save-as-division-favorite toggle,
 *     bold project, "community · subject · deploy" sub-line, "Duplicate →"
 *   - footer status line (live / loading / none-found)
 */
import { useEffect, useRef, useState } from "react";
import { getJobs, getFavorites, favSave, favDelete, getSentEmail } from "../services/api";
import { mvpJobAllow } from "../services/mvpMode";
import { type DupJob, jstr, offerable, isScheduled, dupLS, dupPre, dupPrePut } from "../services/dupJobs";

interface Props {
  div: string;
  onApply: (job: DupJob) => void;
  onClose: () => void;
  /** bottom-center toast (monolith __fvToast) — owned by FormView so apply-side toasts share it */
  notify: (msg: string) => void;
}

export default function DupModal({ div, onApply, onClose, notify }: Props) {
  /* null = nothing cached yet (loading); [] = the worker answered with nothing */
  const [jobs, setJobs] = useState<DupJob[] | null>(() => {
    const p = dupPre(div);
    return p && p.length ? p : dupLS(div);
  });
  const [q, setQ] = useState("");
  const [favSaved, setFavSaved] = useState<Record<string, { id: string }>>({});
  const searchRef = useRef<HTMLInputElement>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    searchRef.current?.focus();
    /* live refresh (background — the cached list is already on screen) */
    getJobs(div, { app: true }).then((d) => {
      if (!alive.current) return;
      if (d && Array.isArray(d.jobs)) { setJobs(d.jobs); dupPrePut(div, d.jobs); }
    }).catch(() => {});
    /* division favorites → ★ states (monolith __favAll) */
    getFavorites().then((d) => {
      if (!alive.current || !d || !d.ok) return;
      const m: Record<string, { id: string }> = {};
      (d.items || []).forEach((f) => { m[(f.div || "") + "|" + (f.name || "")] = { id: f.id }; });
      setFavSaved(m);
    }).catch(() => {});
    return () => { alive.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [div]);

  /* ── SEARCH BY JOB NUMBER (2026-08-21, Lauren: "for duplicate a past job, they
     typically search by job# not job name") ──────────────────────────────────
     A digits-only query (with or without a leading #) is a JOB NUMBER lookup, not
     a text search. Kept as a separate branch on purpose: folding the number into
     the text haystack would make "31" match every subject containing "31", which
     is the opposite of what someone typing a job number wants. Prefix match, so a
     half-typed number narrows as you go and the full number lands one row. */
  const raw = q.trim();
  const numQ = raw.replace(/^#/, "");
  const byNumber = numQ !== "" && /^\d+$/.test(numQ);
  const ql = raw.toLowerCase();
  /* every submitted job, scheduled ones included (2026-10-29) — see dupJobs.offerable */
  const list = offerable(jobs || []).filter(mvpJobAllow).filter((j) => {
    if (!ql) return true;
    if (byNumber) return jstr(j, "jobNumber").indexOf(numQ) === 0;
    /* text search also covers the number, so a mixed query ("65432 lagoon") and a
       paste that carries stray characters still find the job */
    return (jstr(j, "project") + jstr(j, "community") + jstr(j, "subject") + jstr(j, "jobNumber"))
      .toLowerCase().indexOf(ql) > -1;
  });

  /* footer status (monolith renderDupList's dupFoot contract) */
  const foot = jobs && jobs.length
    ? "Live — recent " + (div ? div + " " : "") + "eblast requests from Airtable (Creative Studio Projects)."
    : jobs
      ? "No past jobs found" + (div ? " for " + div : "") + " yet."
      : "Loading live requests… (showing samples until they arrive).";

  /* ☆/★ toggle (monolith dupList .dv click): saved → /fav-delete; unsaved →
     pull the delivered email (/sent-email) then /fav-save it under the division */
  function toggleFav(e: React.MouseEvent, j: DupJob) {
    e.stopPropagation();
    const dv = (jstr(j, "division") || div || "").toUpperCase();
    const key = jstr(j, "division") + "|" + (jstr(j, "project") || jstr(j, "subject"));
    const saved = favSaved[key];
    if (saved && saved.id) {
      notify("Removing from favorites…");
      favDelete(saved.id).then((d) => {
        if (d && d.ok) {
          setFavSaved((m) => { const n = { ...m }; delete n[key]; return n; });
          notify("Removed from favorites.");
        } else notify("Remove failed.");
      }).catch(() => notify("Remove failed — network."));
      return;
    }
    notify("Checking for the delivered email…");
    const dm = jstr(j, "deploy").match(/\d{4}-\d{2}-\d{2}/);
    getSentEmail({ subject: jstr(j, "subject"), div: dv, date: dm ? dm[0] : undefined, name: jstr(j, "project") }).then((sj) => {
      if (!(sj && sj.ok && sj.html && sj.html.length > 500)) {
        notify("No archived email for this job yet — duplicate it, then ★ Save favorite from Design Email.");
        return;
      }
      favSave({ div: dv, name: jstr(j, "project") || jstr(j, "subject") || "Past job", html: sj.html, source: "Past job" }).then((d) => {
        if (d && d.ok) {
          setFavSaved((m) => ({ ...m, [dv + "|" + (jstr(j, "project") || jstr(j, "subject"))]: { id: d.id || "" } }));
          notify("Saved ★ (v" + (d.version ?? 1) + ") — under Division favorites in the template picker.");
        } else notify("Save failed.");
      });
    }).catch(() => notify("Save failed — network."));
  }

  return (
    <div className="rb-ovl" onClick={onClose}>
      <div className="rb-modal rb-dup" onClick={(e) => e.stopPropagation()}>
        <div className="rb-modal-hd">
          <b>Duplicate A Past Job</b>
          {/* job# first in the placeholder — it is how the team actually searches */}
          <input ref={searchRef} className="rb-dup-search" placeholder="Search by job #, name, community, or subject…"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <button onClick={onClose}>×</button>
        </div>
        <div className="rb-dup-list">
          {list.length === 0 && <div className="rb-dup-none">No past jobs match.</div>}
          {list.map((j, i) => {
            const key = jstr(j, "division") + "|" + (jstr(j, "project") || jstr(j, "subject"));
            const sv = !!favSaved[key];
            return (
              <button key={i} className="rb-dup-row" onClick={() => onApply(j)}>
                <span className={"dv" + (sv ? " fav" : "")} title={sv ? "Saved as a division favorite" : "Save as a division favorite"}
                  onClick={(e) => toggleFav(e, j)}>
                  <span className="dvt">{jstr(j, "division") || "—"}</span>
                  <span className={"dfav" + (sv ? " on" : "")}>{sv ? "★" : "☆"}</span>
                </span>
                <span className="dl">
                  <b>{jstr(j, "project") || "Untitled"}{isScheduled(j) && <span className="dsched">Scheduled</span>}</b>
                  {/* job # leads the meta line so a number search can be confirmed by eye.
                      Omitted rather than shown as a blank when the row has none — an older
                      job with no number should not read as "job #—".

                      EVERY PIECE CARRIES ITS OWN SEPARATOR (2026-08-28, BAU). The line is
                      four pieces joined by " · ", and two of them were unguarded: `subject`
                      had no fallback, and `deploy` was tested RAW (`j.deploy`) rather than
                      through `jstr`, so the string "null" was truthy and appended itself.
                      Building it as a filtered join means a missing piece takes its
                      separator with it — no stray "null", no dangling " · ". The job stays
                      in the list and stays duplicable (Reading A). */}
                  <span>{[
                    jstr(j, "jobNumber") ? "Job #" + jstr(j, "jobNumber") : "",
                    jstr(j, "community") || "Market-level",
                    jstr(j, "subject"),
                    jstr(j, "deploy"),
                  ].filter(Boolean).join(" · ")}</span>
                </span>
                <span className="dcp">Duplicate →</span>
              </button>
            );
          })}
        </div>
        <div className="rb-dup-foot">{foot}</div>
      </div>
    </div>
  );
}
